/**
 * Settings → Flint AI route (was "Integrations").
 *
 * Configures the assistant across four sections:
 *   1. Agent     — master on/off + workspace default language (companies.update,
 *                  client-side dirty tracking + SaveBar, mirrors Company profile).
 *   2. Channels  — WhatsApp / Telegram / Slack connections (self-managed).
 *   3. Capabilities — Skills + tool groups (V1.x, disabled-with-reason).
 *   4. Spend & limits — live AI credit usage + (soon) a rupee cap.
 *
 * Only the Agent card is editable through the SaveBar; the other sections own
 * their own state, so the SaveBar sits directly under the Agent card.
 *
 * @see docs/spec/08-system-decisions.md §4–§5 — automation + AI governance
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Alert } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryKey, meQueryOptions } from "../../../../lib/session.ts";
import { formatRelativeTime } from "../../../../lib/format.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, SaveBar, type SaveBarState } from "../../../../features/settings/index.ts";
import {
  AgentCard,
  ChannelsSection,
  CapabilitiesCard,
  SpendCard,
  type AgentCardValues,
} from "../../../../features/settings/assistant/index.ts";

export const Route = createFileRoute("/(workspace)/settings/assistant/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for
  // rationale. Note: the AI add-on module gate is enforced separately in
  // the rail (`ai.assistant` module) and by the AI features themselves.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: FlintAiSettingsPage,
});

const EMPTY_AGENT: AgentCardValues = { aiEnabled: true, aiDefaultLanguage: "" };

function FlintAiSettingsPage() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const company = trpc.companies.get.useQuery({});

  const [baseline, setBaseline] = useState<AgentCardValues>(EMPTY_AGENT);
  const [values, setValues] = useState<AgentCardValues>(EMPTY_AGENT);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Hydrate from the company row; don't clobber an in-flight edit on refetch.
  useEffect(() => {
    if (!company.data) return;
    const next: AgentCardValues = {
      aiEnabled: company.data.aiEnabled,
      aiDefaultLanguage: company.data.aiDefaultLanguage ?? "",
    };
    setBaseline(next);
    setValues((current) => (isDirty(current, baseline) ? current : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.data]);

  const update = trpc.companies.update.useMutation({
    onSuccess: async () => {
      // Refresh both the company row AND the session resolver (`me`) — the
      // latter carries `aiEnabled`, which gates the Ask Flint nav entry +
      // /ask route. The session lives under the plain `meQueryKey` (a vanilla
      // queryFn), NOT tRPC's structured key, so invalidate it directly.
      await Promise.all([
        utils.companies.get.invalidate(),
        queryClient.invalidateQueries({ queryKey: meQueryKey }),
      ]);
      setSavedAt(new Date());
    },
  });

  const dirty = isDirty(values, baseline);

  const saveBarState: SaveBarState = update.isPending
    ? "saving"
    : dirty
      ? "dirty"
      : savedAt
        ? "saved"
        : "idle";

  const statusText = useMemo(() => {
    if (update.isPending) return "Saving…";
    if (dirty) return "Unsaved changes — review before saving";
    if (savedAt) return `All changes saved · ${formatRelativeTime(savedAt)}`;
    return "No changes yet";
  }, [update.isPending, dirty, savedAt]);

  const onSave = useCallback(async () => {
    const payload: Record<string, unknown> = {};
    if (values.aiEnabled !== baseline.aiEnabled) payload.aiEnabled = values.aiEnabled;
    if (values.aiDefaultLanguage !== baseline.aiDefaultLanguage) {
      // "" clears back to auto-detect (null) — the contract accepts the empty
      // string and the domain normalises it.
      payload.aiDefaultLanguage = values.aiDefaultLanguage;
    }
    if (Object.keys(payload).length === 0) return;
    await update.mutateAsync(payload).catch(() => {
      /* surfaced via update.error in the Alert below */
    });
  }, [values, baseline, update]);

  const onDiscard = useCallback(() => setValues(baseline), [baseline]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Flint AI" }]}>
      <SettingsShell
        activeSection="assistant"
        pageTitle="Flint AI"
        pageDescription="Configure your AI assistant — turn it on or off, set its language, connect channels, and track what it costs."
      >
        {company.isLoading ? (
          <p className="text-sm text-muted">Loading Flint AI settings…</p>
        ) : company.error ? (
          <Alert
            intent="bad"
            title="Couldn't load Flint AI settings"
            description={company.error.message}
          />
        ) : (
          <>
            {update.error ? (
              <Alert
                intent="bad"
                title="Couldn't save Flint AI settings"
                description={update.error.message}
              />
            ) : null}

            <AgentCard values={values} onChange={setValues} />
            <SaveBar
              state={saveBarState}
              statusText={statusText}
              onDiscard={onDiscard}
              onSave={() => void onSave()}
            />

            {/* This whole page is gated by the AI add-on (migration 0107), so
                it's only reachable when Flint AI is on — always show config. */}
            <ChannelsSection />
            <CapabilitiesCard />
            <SpendCard />
          </>
        )}
      </SettingsShell>
    </WorkspaceShell>
  );
}

function isDirty(a: AgentCardValues, b: AgentCardValues): boolean {
  return a.aiEnabled !== b.aiEnabled || a.aiDefaultLanguage !== b.aiDefaultLanguage;
}
